import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CESSNA, FLIGHTLOGGER, EXAM, ALTON } from "../data";

export const CessnaBand = ({ withCTA = true }) => (
  <section className="py-24 lg:py-32 bg-navy relative overflow-hidden" data-testid="cessna-band">
    <div className="absolute inset-0 grain" />
    <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[var(--secondary)] opacity-20 blur-[120px]" />
    <div className="max-w-7xl mx-auto px-6 relative grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-4">
        <Reveal>
          <div className="rounded-2xl bg-white p-8 card-shadow-lg inline-flex">
            <img src="/img/cpc.webp" alt="Cessna Pilot Center by Textron Aviation" className="w-full max-w-[240px] object-contain" />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal><Overline dark>Certified · Cessna Pilot Center</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-white leading-[1.05]">{"Africa's"} <span className="italic text-accent-2">certified</span> Cessna Pilot Center.</h2></Reveal>
        <Reveal delay={0.15}><p className="mt-5 text-white/65 text-lg leading-relaxed max-w-2xl">{CESSNA.intro}</p></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-5">
          {CESSNA.points.map((p, i) => (
            <Reveal key={p.title} delay={0.2 + i * 0.06}>
              <div className="flex gap-3">
                <CheckCircle2 size={20} className="text-accent-2 shrink-0 mt-0.5" />
                <div><h3 className="font-display font-semibold text-white">{p.title}</h3><p className="text-white/55 text-sm mt-1 leading-relaxed">{p.text}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
        {withCTA && (
          <Reveal delay={0.4}><Link to="/cessna" data-testid="cessna-band-cta" className="mt-9 btn-primary px-8 py-4 inline-flex items-center gap-2">Discover the CPC difference <ArrowRight size={18} /></Link></Reveal>
        )}
      </div>
    </div>
  </section>
);

export const FlightLoggerSection = () => (
  <section className="py-24 lg:py-32 bg-white" data-testid="flightlogger-section">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <Reveal><Overline>FlightLogger · Certified Partner</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-[var(--primary)] leading-[1.05]">{FLIGHTLOGGER.headline}</h2></Reveal>
        <Reveal delay={0.15}><p className="mt-5 text-[var(--muted)] text-lg leading-relaxed">{FLIGHTLOGGER.intro}</p></Reveal>
        <div className="mt-8 space-y-5">
          {FLIGHTLOGGER.points.map((p, i) => (
            <Reveal key={p.title} delay={0.2 + i * 0.06}>
              <div className="flex gap-4 items-start pb-5 border-b border-[var(--line)] last:border-0">
                <CheckCircle2 size={20} className="text-accent shrink-0 mt-1" />
                <div><h3 className="font-display font-semibold text-[var(--primary)]">{p.title}</h3><p className="text-[var(--muted)] text-sm mt-1 leading-relaxed">{p.text}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="lg:col-span-6 order-1 lg:order-2 space-y-6">
        <Reveal delay={0.1}>
          <div className="rounded-2xl overflow-hidden card-shadow-lg bg-[var(--paper)] p-6 sm:p-8 flex items-center justify-center">
            <img src="/img/flightlogger.png" alt="FlightLogger Certified Partner" className="w-full max-w-sm object-contain rounded-xl" loading="lazy" />
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="rounded-2xl overflow-hidden card-shadow-lg bg-navy relative">
            <video autoPlay muted loop playsInline className="w-full aspect-video object-cover" data-testid="flightlogger-cert-video"><source src="/videos/flcert.mp4" type="video/mp4" /></video>
            <div className="p-5">
              <p className="text-white/85 text-sm"><span className="text-accent-2 font-semibold">Officially certified.</span> Our FlightLogger Certified Partner certificate — proof that Eagle 1 meets the platform's global standard for digital flight-training management.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const ExamCentreSection = () => {
  const icons = [Icons.Building2, Icons.MonitorCheck, Icons.GraduationCap];
  return (
    <section className="py-24 lg:py-32 bg-[var(--paper)]" data-testid="exam-centre-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <Reveal><Overline>Examinations</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-[var(--primary)]">{EXAM.headline}</h2></Reveal>
          <Reveal delay={0.15}><p className="mt-5 text-[var(--muted)] text-lg">{EXAM.intro}</p></Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {EXAM.points.map((p, i) => {
            const Icon = icons[i] || Icons.Check;
            return (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="rounded-2xl bg-white border border-[var(--line)] p-8 card-shadow h-full">
                  <div className="h-14 w-14 rounded-2xl bg-navy flex items-center justify-center"><Icon size={26} className="text-accent-2" /></div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-[var(--primary)]">{p.title}</h3>
                  <p className="mt-3 text-sm text-[var(--muted)] leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const AltonSection = () => (
  <section className="py-24 lg:py-32 bg-navy relative overflow-hidden" data-testid="alton-section">
    <div className="absolute inset-0 grain" />
    <div className="max-w-7xl mx-auto px-6 relative grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <Reveal>
          <div className="rounded-2xl overflow-hidden card-shadow-lg aspect-video bg-black">
            <video autoPlay muted loop playsInline className="w-full h-full object-cover" data-testid="alton-video"><source src="/videos/alton.mp4" type="video/mp4" /></video>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6">
        <Reveal><Overline dark>Engineering Partner</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-white leading-[1.05]">{ALTON.headline}</h2></Reveal>
        <Reveal delay={0.15}><p className="mt-5 text-white/65 text-lg leading-relaxed">{ALTON.intro}</p></Reveal>
        <div className="mt-8 space-y-4">
          {ALTON.points.map((t, i) => (
            <Reveal key={i} delay={0.2 + i * 0.06}>
              <div className="flex gap-3 items-start"><CheckCircle2 size={20} className="text-accent-2 shrink-0 mt-0.5" /><p className="text-white/80">{t}</p></div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const HANGAR_PHOTOS = [
  { src: "/img/hangar2.webp", alt: "Eagle 1 hangar facility exterior" },
  { src: "/img/hangar4.webp", alt: "Hangar with training aircraft" },
  { src: "/img/hangar5.webp", alt: "Modern classroom with interactive smart board" },
  { src: "/img/hangar1.webp", alt: "Reception area" },
  { src: "/img/hangar3.webp", alt: "Accreditations and certificates" },
];
const HANGAR_SPANS = ["sm:col-span-2 sm:row-span-2", "", "", "", ""];

export const HangarSection = () => (
  <section className="py-24 lg:py-32 bg-white" data-testid="hangar-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
        <div className="lg:col-span-7">
          <Reveal><Overline>Our New Home</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-[var(--primary)] leading-[1.05]">A bigger, smarter <span className="italic text-accent">hangar</span>.</h2></Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}><p className="text-[var(--muted)] text-base md:text-lg leading-relaxed">{"We've"} moved into a brand-new, larger facility with a sleek modern design — purpose-built classrooms with interactive smart boards, the latest training technology and comfortable spaces designed around how students actually learn.</p></Reveal>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[150px] sm:auto-rows-[190px] gap-3" data-testid="hangar-gallery">
        {HANGAR_PHOTOS.map((p, i) => (
          <Reveal key={i} delay={(i % 4) * 0.06} className={`${HANGAR_SPANS[i]} overflow-hidden rounded-xl group`}>
            <div className="relative w-full h-full bg-navy">
              <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-[#164275]/0 group-hover:bg-[#164275]/15 transition-colors" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
